import { ColDef, PostSortRowsParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import React, { MutableRefObject, useCallback, useMemo, useRef, useState } from 'react';

import { aggridHeaderClass } from 'src/components/common/common';
import { GridCalendar } from 'src/components/common/form/GridCalendar';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Excel from 'src/components/common/gridWrapper/Grid_Excel';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { formatLocalDate } from 'src/utils/date/dateUtils';
import {
    CreateCellClickHandler,
    CreateCellDoubleClickHandler,
    createCalendarCellClickHandler,
    createCellCssClassRules,
    CustomCellClickAction,
    CustomCellDoubleClickAction,
    GridCellConfig,
    postSortHandler,
    RemoveRowHandler,
    useRowSelectionHandler,
	createGridDropDownCommitAfter,
} from 'src/utils/grid/GridHandlerContent';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags } from 'src/utils/grid/gridStyleUtils';

import {  TQC1020_R01DetailDto } from './Dto_TQC1020_R01';
import { useTQC1020_R01Store } from 'src/containers/mes/TQC/TQC1020_R01/Store_TQC1020_R01';
import { CommonCodeColumn } from 'src/utils/grid/GridColumnContent';
import { useTQC1020_R01Modal } from './hooks/useTQC1020_R01Modal';
import { useTQC1020_R01CellChange } from './hooks/TQC1020_R01CellChange';

const COLUMN_STATE_KEY = 'TQC1020_R01-detail-grid-column-state';

// ── 셀 CSS 설정 (PUR1030 패턴) ──────────────────────────────────────────────
interface Props {
    detailGridRef: MutableRefObject<AgGridReact<TQC1020_R01DetailDto> | null>;
    defaultTQC1020_R01DetailRow: TQC1020_R01DetailDto;
	clickable: boolean;
	setClickable: (clickable: boolean) => void;
	handleInsertClick: (e: React.MouseEvent, rowIndex?: number) => void;
}

export const DetailTable_TQC1020_R01 = ({
	detailGridRef,
	defaultTQC1020_R01DetailRow,
	clickable,
	setClickable,
	handleInsertClick,
}: Props) => {
    const {
        TQC1020_R01DetailList,
        setTQC1020_R01DetailList,
        selectedTQC1020_R01DetailList,
        setSelectedTQC1020_R01DetailList,
        initSelectedTQC1020_R01DetailList,
        deletedTQC1020_R01DetailList,
        addDeletedTQC1020_R01DetailList,
        removeTQC1020_R01DetailList,
        updateTQC1020_R01DetailList,
        originalTQC1020_R01DetailList,
        detailColumnState,
        setDetailColumnState,
        detailScrollTop,
        setDetailScrollTop,
    } = useTQC1020_R01Store();

    const { faultyValueList } = useCommonCodeStore();
    const gridWrapperRef = useRef<HTMLDivElement>(null);
    // 검사일자 셀 클릭 시 띄우는 GridCalendar 상태
    const [gridCalendarState, setGridCalendarState] = useState<{
        pickerVisible: boolean;
        pickerPosition: { top: number; left: number };
        targetRowIndex: number | null;
        targetField: string | null;
        targetDate: Date | undefined;
    }>({
        pickerVisible: false,
        pickerPosition: { top: 0, left: 0 },
        targetRowIndex: null,
        targetField: null,
        targetDate: undefined,
    });

    // ── Grid 공통 설정 ──────────────────────────────────────────────────
    const { getRowId, createRowClassRules, createCellClassRules, paddedGridData, renderIndexCell } =
        useGridSettingContent({
            gridRef: detailGridRef,
            key: 'valueIndex',
            gridData: TQC1020_R01DetailList,
            defaultGridData: defaultTQC1020_R01DetailRow,
            selectedRow: selectedTQC1020_R01DetailList,
            originalGridData: originalTQC1020_R01DetailList,
            updateRow: (index: number, data: TQC1020_R01DetailDto) =>
                updateTQC1020_R01DetailList(index, data),
        });

    const headerNames: Record<string, string> = {
        rowIndex: '',
        inspectorEmp: '검사자',
        inspectorEmpNm: '검사자명',
        inspectDt: '검사일자',
        inspectQty: '검사량',
        badQty: '불량',
        acceptQty: '합격량',
        badCase: '불량사유',
        remark: '비고',
    };

    // ── CSS 클래스 규칙 (PUR1030 패턴) ──────────────────────────────────
    const cssCellConfig = useMemo<GridCellConfig>(() => ({
		// 일반 행에서 항상 비활성
		disabledCells: ['inspectorEmpNm', 'badCaseNm'],
		// 신규(inserted) 행에서 추가 비활성
		insertedDisabledCells: ['inspectNo'],
		// 기존 행에서 추가 비활성
		commonDisabledCells: ['inspectNo', 'acceptQty'],

		// 일반 행에서 포인터 커서
		pointerCells: ['inspectorEmp', 'inspectQty', 'badQty', 'badCase', 'remark'],
		// 신규(inserted) 행에서 추가 포인터
		insertedPointerCells: ['inspectDt'],
		// 기존 행에서 추가 포인터
		commonPointerCells: ['inspectDt'],

		alignLeftCells: ['inspectorEmp', 'badCase', 'remark'],
	}), []);

	const { disabledInsertedColumns, disabledColumns, pointerInsertedColumns, pointerColumns, alignLeftColumns } = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

    const cellCssDefaultColDef = useMemo(() => {
        const baseRules = createCellClassRules<TQC1020_R01DetailDto>({
            idKey: 'valueIndex',
            selectedList: selectedTQC1020_R01DetailList,
            deletedList: deletedTQC1020_R01DetailList,
            disabledInsertedColumns,
            disabledColumns,
            pointerInsertedColumns,
            pointerColumns,
            alignLeftColumns,
        });
        return { ...defaultColDef, cellClassRules: { ...baseRules } };
    }, [
		
        selectedTQC1020_R01DetailList,
        deletedTQC1020_R01DetailList,
        disabledInsertedColumns,
        disabledColumns,
        pointerInsertedColumns,
        pointerColumns,
        alignLeftColumns, createCellClassRules,
	]);

    // ── 셀 클릭 (편집 모드 진입) ─────────────────────────────────────────
    const editableColumns = ['inspectorEmp', 'inspectQty', 'badQty', 'badCase', 'remark'];

    // ── +/- 핸들러 (PUR1030 패턴) ────────────────────────────────────────
	const handleGridDropDownCommitAfter = useMemo(() => 
		createGridDropDownCommitAfter<TQC1020_R01DetailDto>(editableColumns),
	[editableColumns],);

    // ── 컬럼 정의 ────────────────────────────────────────────────────────
    const baseColumnDefs: ColDef<TQC1020_R01DetailDto>[] = useMemo(
        () => [
            ...getDefaultRowIndexColumnConfig((params: TQC1020_R01DetailDto) =>
                renderIndexCell(
                    params,
                    (innerParams: TQC1020_R01DetailDto) =>
                        getRowStyleFlags(innerParams, {
                            deletedList: deletedTQC1020_R01DetailList,
                            selectedList: selectedTQC1020_R01DetailList,
                            dataList: TQC1020_R01DetailList,
                        }),
                    (rowIndex: number, e: React.MouseEvent) => {
                        if (e.ctrlKey) {
                            for (let i = 0; i < 10; i++) handleInsertClick(e, rowIndex);
                        } else {
                            handleInsertClick(e, rowIndex);
                        }
                    },
                    (rowData) =>
                        RemoveRowHandler(
                            detailGridRef,
                            TQC1020_R01DetailList,
                            rowData,
                            setClickable,
                            removeTQC1020_R01DetailList,
                            (valueIndex) => addDeletedTQC1020_R01DetailList([valueIndex.toString()])
                        )
                )
            ),
            {
                headerName: '검사자',
                colId: 'inspectorEmp',
                field: 'inspectorEmp',
                width: 100,
                editable: true,
                pasteable: true,
            },
            {
                headerName: '검사자명',
                colId: 'inspectorEmpNm',
                field: 'inspectorEmpNm',
                width: 100,
                editable: false,
            },
            {
                headerName: '검사일자',
                colId: 'inspectDt',
                field: 'inspectDt',
                width: 110,
                editable: true,
                pasteable: true,
            },
            {
                headerName: '검사량',
                colId: 'inspectQty',
                field: 'inspectQty',
                width: 90,
                editable: true,
                pasteable: true,
                comparator: (a, b) => Number(a) - Number(b),
                valueParser: (params) =>
                    Number(String(params.newValue ?? '').replaceAll(',', '').trim()) || 0,
                valueFormatter: (params) => params.value?.toLocaleString() ?? '',
            },
            {
                headerName: '불량',
                colId: 'badQty',
                field: 'badQty',
                width: 80,
                editable: true,
                pasteable: true,
                comparator: (a, b) => Number(a) - Number(b),
                valueParser: (params) =>
                    Number(String(params.newValue ?? '').replaceAll(',', '').trim()) || 0,
                valueFormatter: (params) => params.value?.toLocaleString() ?? '',
            },
            // {
            //     headerName: '합격량',
            //     colId: 'acceptQty',
            //     field: 'acceptQty',
            //     width: 90,
            //     editable: false,
            //     comparator: (a, b) => Number(a) - Number(b),
            //     valueFormatter: (params) => params.value?.toLocaleString() ?? '',
            // },
            {
                headerName: '불량사유',
                colId: 'badCase',
                field: 'badCase',
                width: 120,
                ...CommonCodeColumn.options(faultyValueList, true, CommonCodeColumn.change(updateTQC1020_R01DetailList), handleGridDropDownCommitAfter),
            },
            {
                headerName: '비고',
                colId: 'remark',
                field: 'remark',
                width: 200,
                minWidth: 140,
                editable: true,
                pasteable: true,
            },
        ],
        [
            deletedTQC1020_R01DetailList,
            selectedTQC1020_R01DetailList,
            TQC1020_R01DetailList,
            faultyValueList,
            renderIndexCell,
            handleInsertClick,
            detailGridRef,
            removeTQC1020_R01DetailList,
            addDeletedTQC1020_R01DetailList,
        ]
    );

    
    const defaultColumnState = useMemo(
    	() => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
    [baseColumnDefs]);

	const columnDefs: ColDef<TQC1020_R01DetailDto>[] = useMemo(() => {
        if (detailColumnState === null) return baseColumnDefs;
        return GridCustomColumnDefs<TQC1020_R01DetailDto>({
            baseColumnDefs,
            columnState: detailColumnState,
            headerNames,
            fallbackHeaderClass: aggridHeaderClass,
        });
    }, [baseColumnDefs, detailColumnState]);

    const { settingInspectorModalProps } = useTQC1020_R01Modal({
        detailGridRef,
        updateTQC1020_R01ResultList: updateTQC1020_R01DetailList,
        TQC1020_R01ResultList: TQC1020_R01DetailList,
        originalTQC1020_R01ResultList: originalTQC1020_R01DetailList,
    });

    const { handleCellChange } = useTQC1020_R01CellChange({
        detailGridRef,
        TQC1020_R01ResultList: TQC1020_R01DetailList,
        originalTQC1020_R01ResultList: originalTQC1020_R01DetailList,
        updateTQC1020_R01ResultList: updateTQC1020_R01DetailList,
        settingInspectorModalProps,
    });

    const addSelectedSingle = useCallback(
        (ids: string[]) => {
            if (ids.length === 0) {
                setSelectedTQC1020_R01DetailList([]);
                return;
            }
            // 상세 그리드는 단일 선택만 유지
            setSelectedTQC1020_R01DetailList([ids[ids.length - 1]]);
        },
        [setSelectedTQC1020_R01DetailList]
    );

    const removeSelectedSingle = useCallback(
        (ids: string[]) => {
            setSelectedTQC1020_R01DetailList(
                selectedTQC1020_R01DetailList.filter((v) => !ids.includes(v))
            );
        },
        [selectedTQC1020_R01DetailList, setSelectedTQC1020_R01DetailList]
    );

    // ── 행 클릭 선택 ─────────────────────────────────────────────────────
    const handleRowClick = useRowSelectionHandler({
        dataList: TQC1020_R01DetailList,
        idKey: 'valueIndex',
        selectedList: selectedTQC1020_R01DetailList,
        deletedList: deletedTQC1020_R01DetailList,
        addSelected: addSelectedSingle,
        removeSelected: removeSelectedSingle,
        clickable,
        setClickable,
    });
    const cellClickActions: CustomCellClickAction<TQC1020_R01DetailDto>[] = useMemo(
        () => [
            {
                // 검사일자는 텍스트 편집 대신 달력 팝업으로 입력
                validate: (field) => field === 'inspectDt',
                action: createCalendarCellClickHandler(
                    'inspectDt',
                    { pickerVisible: gridCalendarState.pickerVisible },
                    (newState) => setGridCalendarState((prev) => ({ ...prev, ...newState }))
                ),
            },
            {
                validate: (field) => editableColumns.includes(field),
                action: (e) => {
                    e.api.startEditingCell({
                        rowIndex: e.rowIndex!,
                        colKey: e.colDef.field!,
                    });
                },
            },
        ],
        [editableColumns, gridCalendarState.pickerVisible]
    );
    const handleCellClick = CreateCellClickHandler(
        TQC1020_R01DetailList,
        'valueIndex',
        cellClickActions
    );

    const cellDoubleClickActions: CustomCellDoubleClickAction<TQC1020_R01DetailDto>[] = useMemo(
        () => [
            {
                // 검사자 컬럼 더블클릭 시 코드검색 모달 오픈
                validate: (field) => field === 'inspectorEmp' || field === 'inspectorEmpNm',
                action: (e, foundData) => {
                    settingInspectorModalProps(
                        String(foundData.inspectorEmp ?? ''),
                        foundData,
                        foundData.valueIndex
                    );
                },
            },
        ],
        [settingInspectorModalProps]
    );

    const handleCellDoubleClick = CreateCellDoubleClickHandler(
        TQC1020_R01DetailList,
        'valueIndex',
        cellDoubleClickActions
    );

    // ── 정렬 후 처리 ─────────────────────────────────────────────────────
    const handlePostSort = useCallback((params: PostSortRowsParams<TQC1020_R01DetailDto>) =>
        {
                postSortHandler(
                params,
                TQC1020_R01DetailList,
                originalTQC1020_R01DetailList,
                setTQC1020_R01DetailList
            );
        },
        [TQC1020_R01DetailList, originalTQC1020_R01DetailList, setTQC1020_R01DetailList]
    );

    // ── 하단 고정 행 (건수 + 합계) ───────────────────────────────────────
    const pinnedBottomRowData = useMemo(() => {
        const totalInspectQty = TQC1020_R01DetailList.reduce(
            (sum: number, item: TQC1020_R01DetailDto) =>
                deletedTQC1020_R01DetailList.includes(item.valueIndex.toString())
                    ? sum
                    : sum + (Number(item.inspectQty) || 0),
            0
        );
        const totalBadQty = TQC1020_R01DetailList.reduce(
            (sum: number, item: TQC1020_R01DetailDto) =>
                deletedTQC1020_R01DetailList.includes(item.valueIndex.toString())
                    ? sum
                    : sum + (Number(item.badQty) || 0),
            0
        );
        return [
            {
                inspectorEmp: TQC1020_R01DetailList.length.toLocaleString() + '건',
                inspectQty: totalInspectQty,
                badQty: totalBadQty,
                acceptQty: Math.max(0, totalInspectQty - totalBadQty),
            } as TQC1020_R01DetailDto,
        ];
    }, [TQC1020_R01DetailList, deletedTQC1020_R01DetailList]);

    return (
        <GridContextWrapper<TQC1020_R01DetailDto>
        	enableCellCalculation
            gridWrapperRef={gridWrapperRef}
            gridRef={detailGridRef}
            paddedGridData={paddedGridData}
            setPaddedGridData={setTQC1020_R01DetailList}
            initSelected={initSelectedTQC1020_R01DetailList}
			defaultRowData={defaultTQC1020_R01DetailRow}
            baseColumnDefs={detailColumnState ? columnDefs : baseColumnDefs}
            defaultColumnState={defaultColumnState}
            setColumnState={setDetailColumnState}
            localStorageKey={COLUMN_STATE_KEY}
            menuItems={undefined}
        >
            <Grid_Excel<TQC1020_R01DetailDto>
                ref={detailGridRef}
                rowData={paddedGridData}
                getRowId={getRowId}
                columnDefs={columnDefs}
                defaultColDef={cellCssDefaultColDef}
                rowClassRules={createRowClassRules<TQC1020_R01DetailDto>({
                    idKey: 'valueIndex',
                    selectedList: selectedTQC1020_R01DetailList,
                    deletedList: deletedTQC1020_R01DetailList,
                })}
                onRowClicked={handleRowClick as any}
                onCellClicked={handleCellClick}
                onCellDoubleClicked={handleCellDoubleClick}
                onCellValueChanged={handleCellChange as any}
                pinnedBottomRowData={pinnedBottomRowData}
                onPostSortRows={handlePostSort}
                columnStateKey={COLUMN_STATE_KEY}
                setColumnState={setDetailColumnState}
                getDefaultColumnState={() => defaultColumnState}
                scrollTop={detailScrollTop}
                setScrollTop={setDetailScrollTop}
            />
            <GridCalendar
                rowIndex={gridCalendarState.targetRowIndex ?? 0}
                targetField={gridCalendarState.targetField ?? 'inspectDt'}
                visible={gridCalendarState.pickerVisible}
                setVisible={(visible) => setGridCalendarState((prev) => ({ ...prev, pickerVisible: visible }))}
                value={gridCalendarState.targetDate}
                pickerPosition={gridCalendarState.pickerPosition}
                setPickerPosition={(position) =>
                    setGridCalendarState((prev) => ({ ...prev, pickerPosition: position }))
                }
                onClear={() => {
                    setGridCalendarState((prev) => ({ ...prev, targetDate: undefined }));
                }}
                onChange={(date, rowIndex, targetField) => {
                    if (targetField !== 'inspectDt') return;
                    const foundData = TQC1020_R01DetailList[rowIndex];
                    if (!foundData) return;

                    // 캘린더 결과를 YYYY-MM-DD 문자열로 저장
                    const newDateString = date ? formatLocalDate(date) : '';
                    const originalRow = originalTQC1020_R01DetailList.find(
                        (item) => item.valueIndex === foundData.valueIndex
                    );
                    const isSameAsOriginal = !!originalRow && String(originalRow.inspectDt ?? '') === newDateString;

                    updateTQC1020_R01DetailList(foundData.valueIndex, {
                        ...foundData,
                        inspectDt: newDateString,
                        isModified: foundData.inserted ? false : !isSameAsOriginal,
                    });
                }}
                onClickLoad={(rowIndex, targetField) => {
                    if (targetField !== 'inspectDt') return;
                    const foundData = TQC1020_R01DetailList[rowIndex];
                    if (!foundData?.inspectDt) {
                        setGridCalendarState((prev) => ({ ...prev, targetDate: new Date() }));
                        return;
                    }
                    const parsed = new Date(foundData.inspectDt);
                    setGridCalendarState((prev) => ({
                        ...prev,
                        targetDate: Number.isNaN(parsed.getTime()) ? new Date() : parsed,
                    }));
                }}
            />
        </GridContextWrapper>
    );
};
